import { useEffect, useMemo, useState } from 'react'
import { Pencil } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { cn } from '@/lib/utils'
import type { ServiceChargeOverride } from '@/types'

export interface ServiceChargePopoverProps {
  /** Service charge amount currently in effect (override or settings-derived). */
  currentAmount: number
  /**
   * Service rate currently in effect as a fraction (0.10 == 10%). 0 when the
   * order carries a fixed-amount override or the order type excludes service.
   */
  currentRate: number
  /** The active override, or null when the settings rate applies. */
  override: ServiceChargeOverride | null
  /** Settings rate for this order type as a fraction — restored by "Reset". */
  defaultRate: number
  /** Apply an override (percent or fixed amount). */
  onApply: (next: ServiceChargeOverride) => void
  /** Clear the override, reverting to the settings rate. */
  onReset: () => void
  /** Gates Apply/Reset while a mutation is in flight. */
  pending?: boolean
  disabled?: boolean
}

type Mode = 'rate' | 'amount'

const fmt = (n: number) =>
  n.toLocaleString('en-PK', { minimumFractionDigits: 2, maximumFractionDigits: 2 })

/** Fraction → percent string for the input, trimming float noise (0.125 → "12.5"). */
const pctStr = (fraction: number) => String(Math.round(fraction * 10000) / 100)

/**
 * Inline editor for the per-order service charge. Sibling of DeliveryFeePopover:
 * a small pencil trigger opens a popover with a %/Rs mode toggle, one field,
 * Apply, and (when an override is active) Reset-to-default.
 *
 * Percent mode replaces the settings rate for this order only — the amount
 * still scales with the bill and the receipt prints "(X%)". Amount mode fixes
 * the figure outright; the receipt then prints "Service Charges" with NO
 * percentage, so a stale "(10%)" can never sit beside a number that isn't
 * 10% of anything.
 */
export function ServiceChargePopover({
  currentAmount,
  currentRate,
  override,
  defaultRate,
  onApply,
  onReset,
  pending,
  disabled,
}: ServiceChargePopoverProps) {
  const [open, setOpen] = useState(false)
  const [mode, setMode] = useState<Mode>(override?.kind ?? 'rate')
  const [value, setValue] = useState<string>('')

  // Re-seed mode + field from the live value each time the popover opens so a
  // stale edit can't overwrite a charge changed via another path.
  useEffect(() => {
    if (!open) return
    const m: Mode = override?.kind ?? 'rate'
    setMode(m)
    setValue(m === 'amount' ? String(currentAmount ?? 0) : pctStr(currentRate ?? 0))
  }, [open, override, currentAmount, currentRate])

  // Switching mode re-seeds the field with the equivalent of what's in effect.
  const switchMode = (m: Mode) => {
    if (m === mode) return
    setMode(m)
    setValue(m === 'amount' ? String(currentAmount ?? 0) : pctStr(currentRate ?? 0))
  }

  const parsed = useMemo(() => {
    const n = parseFloat(value)
    return Number.isFinite(n) ? n : NaN
  }, [value])

  const error = useMemo<string | null>(() => {
    if (value.trim() === '') return mode === 'rate' ? 'Enter a percentage' : 'Enter an amount'
    if (!Number.isFinite(parsed) || parsed < 0) {
      return mode === 'rate' ? 'Enter 0–100 (0 = none)' : 'Enter a positive number (0 = waived)'
    }
    if (mode === 'rate' && parsed > 100) return 'Enter 0–100 (0 = none)'
    return null
  }, [mode, parsed, value])

  const canApply = !pending && !error && Number.isFinite(parsed) && parsed >= 0

  const handleApply = () => {
    if (!canApply) return
    onApply(
      mode === 'rate'
        ? { kind: 'rate', value: Math.round(parsed * 100) / 10000 } // percent → fraction, 2dp of a percent
        : { kind: 'amount', value: Math.round(parsed * 100) / 100 },
    )
    setOpen(false)
  }

  const handleReset = () => {
    onReset()
    setOpen(false)
  }

  const overridden = override != null
  const title = overridden
    ? override.kind === 'amount'
      ? `Edit service charge (fixed Rs ${fmt(override.value)})`
      : `Edit service charge (custom ${pctStr(override.value)}%)`
    : 'Edit service charge'

  return (
    <Popover open={open} onOpenChange={(o) => !disabled && setOpen(o)}>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          title={title}
          aria-label="Edit service charge"
          className={cn(
            'inline-flex h-5 w-5 items-center justify-center rounded border transition-colors',
            overridden
              ? 'border-amber-300 bg-amber-50 text-amber-900 hover:bg-amber-100 dark:border-amber-700 dark:bg-amber-950/50 dark:text-amber-200'
              : 'border-border bg-background text-muted-foreground hover:bg-muted',
            disabled && 'cursor-not-allowed opacity-50',
          )}
        >
          <Pencil className="h-3 w-3" aria-hidden />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-64" align="end">
        <div className="space-y-3">
          <div className="flex items-baseline justify-between">
            <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Service charge
            </Label>
            <span className="text-[11px] text-muted-foreground">Default {pctStr(defaultRate)}%</span>
          </div>
          <div
            role="radiogroup"
            aria-label="Service charge mode"
            className="grid grid-cols-2 rounded-md border border-border p-0.5 text-xs"
          >
            {(['rate', 'amount'] as const).map((m) => (
              <button
                key={m}
                type="button"
                role="radio"
                aria-checked={mode === m}
                onClick={() => switchMode(m)}
                className={cn(
                  'h-7 rounded-[5px] font-medium transition-colors',
                  mode === m
                    ? 'bg-foreground text-background'
                    : 'text-muted-foreground hover:bg-muted',
                )}
              >
                {m === 'rate' ? 'Percent %' : 'Amount Rs'}
              </button>
            ))}
          </div>
          <div className="relative">
            <Input
              inputMode="decimal"
              autoFocus
              className={cn('h-9 text-sm', mode === 'rate' ? 'pr-7' : 'pl-8')}
              placeholder={mode === 'rate' ? '10' : '0.00'}
              value={value}
              onChange={(e) => setValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && canApply) {
                  e.preventDefault()
                  handleApply()
                }
              }}
            />
            <span
              aria-hidden
              className={cn(
                'pointer-events-none absolute top-1/2 -translate-y-1/2 text-xs text-muted-foreground',
                mode === 'rate' ? 'right-3' : 'left-3',
              )}
            >
              {mode === 'rate' ? '%' : 'Rs'}
            </span>
          </div>
          {error ? (
            <p className="text-[11px] text-destructive">{error}</p>
          ) : mode === 'rate' ? (
            <p className="text-[11px] text-muted-foreground">
              Replaces the default % on this order. Receipt shows the new %.
            </p>
          ) : (
            <p className="text-[11px] text-muted-foreground">
              Fixed amount for this order. Receipt shows no percentage.
            </p>
          )}
          <div className="flex items-center gap-2 pt-1">
            <Button
              type="button"
              size="sm"
              className="h-8 flex-1"
              disabled={!canApply}
              onClick={handleApply}
            >
              Apply
            </Button>
            {overridden && (
              <Button
                type="button"
                size="sm"
                variant="ghost"
                className="h-8 px-2 text-xs"
                disabled={pending}
                onClick={handleReset}
              >
                Reset
              </Button>
            )}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  )
}
